<template>
    <v-container fluid class="pa-0">
        <v-row class="align-center pl-3">
            <!-- Breadcrumbs -->
            <v-breadcrumbs
                :items="[
                    { text: 'Home', disable: false, href: '/#/' },
                    { text: 'Cost Management', disabled: true }
                ]"
                divider="/"
            ></v-breadcrumbs>
            <v-spacer />
        </v-row>

        <!-- General cost -->
        <v-card :key="greenhouse.id" class="mt-3 mx-3">
            <h2 class="grey--text ml-3 text-center">General cost management</h2>

            <v-row class="ma-0" align="stretch" >
                <!-- Left panel -->
                <v-col class="col-12 col-md-4" align="center">
                    <v-card outlined elevation="3" height="100%">
                        <h5 class="text-center my-3">Greenhouse Space Usage</h5>
                        <v-divider></v-divider>

                        <v-progress-circular
                            :rotate="-90"
                            :size="200"
                            :width="20"
                            :value="greenhouse.useSpacePercent"
                            color="primary"
                            class="mt-3"
                        >
                            {{ greenhouse.useSpacePercent }}%
                        </v-progress-circular>

                        <v-slider
                            v-model="greenhouse.useSpacePercent"
                            color="primary"
                            hide-details=""
                            track-color="grey"
                            class="mt-3 mx-5"
                        ></v-slider>

                        <p class="text-center">
                            <span class="primary--text">
                                {{
                                    (greenhouse.space *
                                        greenhouse.useSpacePercent) /
                                        100
                                }}
                                m2
                            </span>
                            / {{ greenhouse.space }} m2
                        </p>
                    </v-card>
                </v-col>
                <!-- Middle panel -->
                <v-col class="col-12 col-md-4">
                    <v-card outlined elevation="3" height="100%">
                        <h5 class="text-center my-3">General Cost Rate</h5>
                        <v-divider></v-divider>

                        <v-list dense>
                            <v-list-item
                                v-for="item in greenhouse.generalCosts"
                                :key="item.name"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ item.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        €{{
                                            (
                                                item.cost /
                                                greenhouse.space /
                                                greenhouse.availableWeeks
                                            ).toFixed(5)
                                        }}
                                        / m2.wk
                                    </v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action>
                                    €{{ item.cost }} / year
                                    <v-list-item-action-text>
                                        Edit
                                    </v-list-item-action-text>
                                </v-list-item-action>
                            </v-list-item>

                            <v-list-item>
                                <v-spacer></v-spacer>
                                <v-btn outlined small block color="grey"
                                    >Add</v-btn
                                >
                                <v-spacer></v-spacer>
                            </v-list-item>

                            <v-divider></v-divider>

                            <!-- total -->
                            <v-list-item>
                                <span class="subtitle-2"
                                    >Genaral Cost Rate</span
                                >
                                <v-spacer></v-spacer>
                                <h3 class="primary--text">
                                    €{{ generalCostRate.toFixed(5) }}
                                    / m2.wk
                                </h3>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </v-col>

                <!-- Right panel -->
                <v-col class="col-12 col-md-4">
                    <v-card outlined elevation="3" height="100%">
                        <h5 class="text-center my-3">Material Cost Rate</h5>
                        <v-divider></v-divider>

                        <v-list dense>
                            <v-list-item
                                v-for="item in materialCostRates"
                                :key="item.name"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ item.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        Description
                                    </v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action>
                                    €{{ item.cost.toFixed(2) }} /
                                    {{ item.unit }}
                                    <v-list-item-action-text>
                                        Edit
                                    </v-list-item-action-text>
                                </v-list-item-action>
                            </v-list-item>

                            <v-list-item>
                                <v-spacer></v-spacer>
                                <v-btn outlined small block color="grey"
                                    >Add</v-btn
                                >
                                <v-spacer></v-spacer>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </v-col>
            </v-row>
        </v-card>

        <v-row class="ma-0">
            <!-- Plants & Products ROI -->
            <v-col class="col-12">
                <v-tabs>
                    <v-tab>
                        Plants ROI
                    </v-tab>
                    <v-tab>
                        Products ROI
                    </v-tab>
                    <!-- Plants ROI -->
                    <v-tab-item>
                        <v-card>
                            <v-container fluid>
                                <v-data-table
                                    :headers="plantHeaders"
                                    :items="plants"
                                    :footer-props="{
                                        'items-per-page-options': [
                                            10,
                                            25,
                                            50,
                                            100,
                                            -1
                                        ]
                                    }"
                                    sortBy="name"
                                    loading-text="Loading... Please wait"
                                >
                                    <template v-slot:item.potSize="{ item }">
                                        {{ item.potSize }} cm
                                    </template>
                                    <template v-slot:item.avgSpace="{ item }">
                                        {{ item.avgSpace.toFixed(3) }} m2
                                    </template>
                                    <template v-slot:item.spaceWeeks="{ item }">
                                        {{
                                            (
                                                item.totalGrowthDays *
                                                item.avgSpace
                                            ).toFixed(3)
                                        }}
                                        m2.days
                                    </template>
                                    <template
                                        v-slot:item.generalCostAmount="{ item }"
                                    >
                                        €{{
                                            (
                                                item.generalCostAmount *
                                                generalCostRate
                                            ).toFixed(3)
                                        }}
                                    </template>
                                    <template
                                        v-slot:item.specificCostAmount="{
                                            item
                                        }"
                                    >
                                        €{{
                                            item.specificCostAmount.toFixed(3)
                                        }}
                                    </template>
                                    <template
                                        v-slot:item.estimatedSalesPrice="{
                                            item
                                        }"
                                    >
                                        €{{
                                            item.estimatedSalesPrice.toFixed(3)
                                        }}
                                    </template>
                                    <template
                                        v-slot:item.roi="{
                                            item
                                        }"
                                    >
                                        <span class="primary--text">
                                            €{{
                                                (
                                                    item.estimatedSalesPrice -
                                                    item.specificCostAmount -
                                                    item.generalCostAmount *
                                                        generalCostRate
                                                ).toFixed(3)
                                            }}
                                        </span>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-card>
                    </v-tab-item>
                    <!-- /Plants ROI -->

                    <!-- Products ROI -->
                    <v-tab-item>
                        <v-card>
                            <v-container fluid>
                                <v-data-table
                                    :headers="productHeaders"
                                    :items="products"
                                    :footer-props="{
                                        'items-per-page-options': [
                                            10,
                                            25,
                                            50,
                                            100,
                                            -1
                                        ]
                                    }"
                                    sortBy="name"
                                    loading-text="Loading... Please wait"
                                >
                                    <template v-slot:item.potSize="{ item }">
                                        {{ item.potSize }} cm
                                    </template>
                                    <template
                                        v-slot:item.generalCostAmount="{ item }"
                                    >
                                        €{{
                                            (
                                                item.generalCostAmount *
                                                generalCostRate
                                            ).toFixed(3)
                                        }}
                                    </template>
                                    <template
                                        v-slot:item.specificCostAmount="{
                                            item
                                        }"
                                    >
                                        €{{
                                            item.specificCostAmount.toFixed(3)
                                        }}
                                    </template>
                                    <template
                                        v-slot:item.estimatedSalesPrice="{
                                            item
                                        }"
                                    >
                                        €{{
                                            item.estimatedSalesPrice.toFixed(3)
                                        }}
                                    </template>
                                    <template
                                        v-slot:item.roi="{
                                            item
                                        }"
                                    >
                                        <span class="primary--text">
                                            €{{
                                                (
                                                    item.estimatedSalesPrice -
                                                    item.specificCostAmount -
                                                    item.generalCostAmount *
                                                        generalCostRate
                                                ).toFixed(3)
                                            }}
                                        </span>
                                    </template>
                                </v-data-table>
                            </v-container>
                        </v-card>
                    </v-tab-item>
                    <!-- /Products ROI -->
                </v-tabs>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
// import gql from "graphql-tag";
// import moment from "moment";
import helper from "@/utils/helper.js";

export default {
    apollo: {},

    data() {
        return {
            selectedYear: 2021,
            years: [2019, 2020, 2021],
            greenhouse: {
                id: 1,
                name: "Greenhouse 1",
                useSpacePercent: 90,
                space: 100000,
                availableWeeks: 53, // phase 1: support greenhouse level. phase 2: support department level
                generalCosts: [
                    { name: "Energy", cost: 30000 },
                    { name: "General management", cost: 50000 },
                    { name: "Marketing budget", cost: 30000 }
                ]
            },
            plants: [
                {
                    id: 1,
                    name: "Plant 1",
                    potSize: 12,
                    totalGrowthDays: 10,
                    avgSpace: 0.123,
                    spaceWeeks: 0.253, // m2.wk
                    generalCostAmount: 2,
                    specificCostAmount: 0.302,
                    estimatedSalesPrice: 1.614,
                    ROI: 2.1
                },
                {
                    id: 2,
                    name: "Plant 2",
                    potSize: 9,
                    totalGrowthDays: 8,
                    avgSpace: 0.221,
                    spaceWeeks: 0.157, // m2.wk
                    generalCostAmount: 3,
                    specificCostAmount: 0.311,
                    estimatedSalesPrice: 0.923,
                    ROI: 2.1
                },
                {
                    id: 3,
                    name: "Plant 3",
                    potSize: 7,
                    totalGrowthDays: 5,
                    avgSpace: 0.425,
                    spaceWeeks: 0.138, // m2.wk
                    generalCostAmount: 1,
                    specificCostAmount: 0.223,
                    estimatedSalesPrice: 0.561,
                    ROI: 2.1
                }
            ],

            // table headers
            plantHeaders: [
                {
                    text: "Plant",
                    value: "name",
                    sortable: true
                },
                {
                    text: "Pot Szie",
                    value: "potSize",
                    sortable: true
                },
                {
                    text: "#Weeks",
                    value: "totalGrowthDays",
                    sortable: true
                },
                {
                    text: "Avg. Space",
                    value: "avgSpace",
                    sortable: true
                },
                {
                    text: "Space Weeks",
                    value: "spaceWeeks",
                    sortable: true
                },
                {
                    text: "General Cost",
                    value: "generalCostAmount",
                    sortable: true
                },
                {
                    text: "Specific Cost",
                    value: "specificCostAmount",
                    sortable: true
                },
                {
                    text: "≈Sale Price",
                    value: "estimatedSalesPrice",
                    sortable: true
                },
                {
                    text: "ROI / m2.wk",
                    value: "roi",
                    sortable: true
                }
            ],

            products: [
                {
                    id: 1,
                    name: "Product 1",
                    potSize: 12,
                    plants: 4,
                    generalCostAmount: 1,
                    specificCostAmount: 0.223,
                    estimatedSalesPrice: 1.614,
                    ROI: 2.1
                }
            ],

            // table headers
            productHeaders: [
                {
                    text: "Product",
                    value: "name",
                    sortable: true
                },
                {
                    text: "Pot Szie",
                    value: "potSize",
                    sortable: true
                },
                {
                    text: "#Plants",
                    value: "plants",
                    sortable: true
                },
                {
                    text: "General Cost",
                    value: "generalCostAmount",
                    sortable: true
                },
                {
                    text: "Specific Cost",
                    value: "specificCostAmount",
                    sortable: true
                },
                {
                    text: "≈Sale Price",
                    value: "estimatedSalesPrice",
                    sortable: true
                },
                {
                    text: "ROI / m2.wk",
                    value: "roi",
                    sortable: true
                }
            ],

            // material cost rate
            materialCostRates: [
                { id: 1, name: "Manure A", cost: 0.231, unit: "KG" },
                { id: 2, name: "Manure B", cost: 0.125, unit: "L" },
                { id: 3, name: "Labor", cost: 22, unit: "hour" }
            ]
        };
    },
    computed: {
        totalGeneralCost() {
            var total = this.greenhouse.generalCosts.reduce(function(
                accumulator,
                item
            ) {
                return accumulator + parseInt(item.cost);
            },
            0);

            return total;
        },
        generalCostRate() {
            return (
                this.totalGeneralCost /
                ((this.greenhouse.space * this.greenhouse.useSpacePercent) /
                    100) /
                this.greenhouse.availableWeeks
            );
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
        // this.initialize();
    },
    methods: {}
};
</script>